import React, { useState } from "react";
import api from "@/lib/api";
import { X, Coins, Sparkle, Loader2 } from "lucide-react";

const QUICK_AMOUNTS = [50, 100, 250];
const BONUS_THRESHOLD = 100;
const BONUS_PCT = 0.05;

/**
 * The Maison Funding Station — "Add Balance" to the Maison Wallet via a real Stripe Checkout
 * charge (exact amount chosen/entered, no fee — it's the user's own money). Pre-loading any
 * amount OVER $100 grants a +5% Sovereign Bonus, credited only after payment is confirmed.
 * Triggered ONLY from the Global App Header wallet ticker (AppShell.js) — per the UI Polish
 * directive, the button no longer lives in MaisonWallet.js's own section header.
 */
const WalletTopUpModal = ({ open, onClose }) => {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const effectiveAmount = custom ? parseFloat(custom) || 0 : amount;
  const bonus = effectiveAmount > BONUS_THRESHOLD ? Math.round(effectiveAmount * BONUS_PCT * 100) / 100 : 0;

  const submit = async () => {
    if (!effectiveAmount || effectiveAmount < 5) { setError("Minimum top-up is $5."); return; }
    setLoading(true); setError("");
    try {
      const res = await api.post("/wallet/topup", { amount: effectiveAmount });
      if (res.data.mode === "live" && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      onClose?.(true); // demo mode already credited — tell parent to refresh
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError((d && typeof d === "object" ? d.message : d) || "Could not start the top-up.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-[hsl(38_30%_12%_/_0.6)] backdrop-blur-sm" data-testid="wallet-topup-modal" onClick={() => onClose?.(false)}>
      <div className="surface rounded-[2rem] w-full max-w-sm p-7 relative animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <button data-testid="wallet-topup-close" onClick={() => onClose?.(false)} className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:bg-muted/50"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-2 justify-center">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Coins className="w-5 h-5 text-white" /></div>
        </div>
        <h3 className="font-serif text-2xl text-center mb-1">Add Balance</h3>
        <p className="text-sm text-muted-foreground text-center mb-5">Fuel your Maison Wallet — spend it on Credit Packs anytime.</p>

        <div className="grid grid-cols-3 gap-2 mb-3">
          {QUICK_AMOUNTS.map((a) => (
            <button key={a} type="button" data-testid={`topup-amount-${a}`} onClick={() => { setAmount(a); setCustom(""); }}
              className={`rounded-xl py-3 text-sm font-medium border ${!custom && amount === a ? "bg-primary/15 text-primary border-primary/30" : "surface text-foreground hover:border-primary/30"}`}>
              ${a}
            </button>
          ))}
        </div>
        <input data-testid="topup-custom-amount" type="number" min="5" value={custom} onChange={(e) => setCustom(e.target.value)}
          placeholder="Other amount ($)"
          className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-ring/40" />

        <p className="text-xs text-muted-foreground text-center mb-4 flex items-center justify-center gap-1.5" data-testid="wallet-bonus-note">
          <Sparkle className="w-3.5 h-3.5 gold-text shrink-0" /> Pre-loading any amount over $100 grants a <span className="gold-text font-medium">5% Sovereign Bonus</span> to your Maison Wallet.
        </p>

        {bonus > 0 && (
          <div className="text-sm text-center gold-text mb-3" data-testid="topup-bonus-preview">
            You&apos;ll receive ${(effectiveAmount + bonus).toFixed(2)} (${effectiveAmount.toFixed(2)} + ${bonus.toFixed(2)} bonus)
          </div>
        )}
        {error && <div data-testid="wallet-topup-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">{error}</div>}

        <button data-testid="wallet-topup-submit" onClick={submit} disabled={loading || !effectiveAmount}
          className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-xl py-3 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Continue to Checkout — $${effectiveAmount.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
};

export default WalletTopUpModal;
